import type { AttendanceFlag, AttendanceSource, DayLeave, DriverPayItem, DriverPayType, DriverPolicy, Division, PunchMode, WorkGroup, WorkOrderKind, Diligence, ViolationKind, ViolationStatus, DayMarkKind, DayMarkStatus } from '@mt/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { prepareReportPhoto } from '@/lib/report-photo';
import type { Paged } from '@/lib/types';
import type { Employee } from './EmployeesPage';

/** P21 B4a chấm công theo nhóm lao động (docs/phases/P21.md §4.3). */
export const ATT_KEY = ['attendance'] as const;

export interface DayRecordView {
  id: number;
  employeeId: number;
  workDate: string;
  checkIn: string | null;
  checkOut: string | null;
  pmCheckIn: string | null;
  pmCheckOut: string | null;
  source: AttendanceSource;
  dayFraction: number | null;
  otHours: number;
  site: string | null;
  flags: string[];
  note: string | null;
  photoInId: number | null;
  photoOutId: number | null;
  adjustReason: string | null;
  version: number;
}

export interface CrewLite {
  id: number;
  name: string;
  memberCount: number;
}

export interface MyAttendance {
  employee: { id: number; code: string; fullName: string; workGroup: WorkGroup } | null;
  today: string;
  record: DayRecordView | null;
  schedule: {
    id: number;
    name: string;
    calcMode: 'hourly' | 'daily';
    startTime: string | null;
    endTime: string | null;
    breakStart: string | null;
    breakMinutes: number;
    punchMode: PunchMode;
    gateInOpen: string | null;
    gateInClose: string | null;
    gateOutOpen: string | null;
    gateOutClose: string | null;
    requirePhoto: boolean;
    requireGps: boolean;
    /** Giờ hết công chuẩn (DEC-532) */
    creditEndTime?: string | null;
    /** DEC-820: NV thuộc nhóm được về sớm hôm nay → được về từ giờ này */
    earlyLeaveTime?: string | null;
  } | null;
  /** Lượt chấm kế tiếp thuộc buổi nào (chấm 2 lần/ngày) */
  session: 'am' | 'pm';
  crews: CrewLite[];
  /** DEC-618: tên các điểm chấm công mình là người xác nhận công */
  confirmsLocations?: string[];
  /** WiFi công ty đang dùng (máy chủ nhận ra theo IP); null = mạng khác / 4G */
  wifi: { id: number; name: string } | null;
  /** Lịch của mình bắt buộc chấm bằng WiFi công ty */
  requireWifi: boolean;
  /** DEC-620: hôm nay được điều đi (đơn / lệnh điều động) → chấm vào, ra tại nơi được điều tới, chụp ảnh + GPS */
  dispatchToday?: boolean;
  /** Tài xế / phụ xe: chấm ra từ giờ này (HH:MM) là đủ công theo quy tắc tài xế */
  driverFullFrom?: string | null;
}

export interface WifiNetwork {
  id: number;
  name: string;
  publicIp: string;
  locationId: number | null;
  locationName: string | null;
  isActive: boolean;
  version: number;
  updatedAt: string;
}

export interface Crew {
  id: number;
  name: string;
  leaderUserId: number;
  leaderName: string | null;
  workGroup: WorkGroup;
  defaultSite: string | null;
  isActive: boolean;
  version: number;
  memberCount: number;
}

/** DEC-820: nhóm được về sớm */
export interface EarlyLeaveGroup {
  id: number;
  name: string;
  leaveTime: string;
  /** 0 = CN … 6 = T7 */
  weekdays: number[];
  note: string | null;
  isActive: boolean;
  version: number;
  members: { employeeId: number; code: string; fullName: string; fromDate: string }[];
}

/** NV đang làm (tối đa 200) cho hộp chọn người: tổ chấm hộ, nhóm được về sớm */
export const useActiveEmployees = () =>
  useQuery({
    queryKey: ['hr', 'employees', 'active-all'],
    queryFn: () => api.get<Paged<Employee>>('/hr/employees', { status: 'active', pageSize: 200 }),
    staleTime: 60_000,
  });

export const useEarlyLeaveGroups = () =>
  useQuery({ queryKey: [...ATT_KEY, 'early-leave-groups'], queryFn: () => api.get<EarlyLeaveGroup[]>('/hr/early-leave-groups') });

export interface CrewMember {
  id: number;
  code: string;
  fullName: string;
  phone: string | null;
  facePhotoId: number | null;
  employmentType: string;
  checkIn: string | null;
  checkOut: string | null;
  dayFraction: number | null;
}

export interface CrewToday {
  crew: Crew;
  today: string;
  members: CrewMember[];
  sessions: { id: number; kind: 'in' | 'out'; punchedAt: string; site: string | null; photoId: number | null; presentCount: number; absentCount: number }[];
}

export interface Schedule {
  id: number;
  code: string;
  name: string;
  calcMode: 'hourly' | 'daily';
  startTime: string | null;
  endTime: string | null;
  breakStart: string | null;
  breakMinutes: number;
  weeklyOff: number[];
  lateGraceMinutes: number;
  requireWifi: boolean;
  defaultForGroup: WorkGroup | null;
  isActive: boolean;
  version: number;
  punchMode: PunchMode;
  gateInOpen: string | null;
  gateInClose: string | null;
  gateOutOpen: string | null;
  gateOutClose: string | null;
  earlyGraceMinutes: number;
  requirePhoto: boolean;
  requireGps: boolean;
  autoCloseMinutes: number | null;
  /** Giờ hết công chuẩn (DEC-532); null = giờ ra */
  creditEndTime: string | null;
  /** Số NV đang làm dùng lịch này hôm nay */
  employeeCount: number;
}

export interface TimesheetCell {
  work: number;
  workedMinutes: number | null;
  lateMinutes: number;
  earlyMinutes: number;
  afterMinutes: number;
  otHours: number;
  offDay: boolean;
  flags: AttendanceFlag[];
  recordId: number | null;
  version: number | null;
  source: AttendanceSource | null;
  checkIn: string | null;
  checkOut: string | null;
  pmCheckIn: string | null;
  pmCheckOut: string | null;
  pmPhotoInId: number | null;
  pmPhotoOutId: number | null;
  latIn: number | null;
  lngIn: number | null;
  latOut: number | null;
  lngOut: number | null;
  dayFraction: number | null;
  site: string | null;
  note: string | null;
  adjustReason: string | null;
  photoInId: number | null;
  photoOutId: number | null;
  wifiInName: string | null;
  wifiOutName: string | null;
  sessions: { am: number; pm: number } | null;
  /** P21 PR 4 – đơn nghỉ đã duyệt phủ ngày này */
  leave?: DayLeave | null;
  /** Đơn xác thực đã duyệt (DEC-246): đi trễ / về nửa chừng hợp lý (phút vẫn tính); điều đi = đủ công */
  /** DEC-246: đơn xác thực đã duyệt (đi trễ / về nửa chừng / điều đi) */
  excuse?: { id: number; kind: 'late' | 'early' | 'dispatch'; label: string }[] | null;
  /** DEC-249: lệnh tăng ca / Chủ nhật / điều động đã xác nhận */
  orders?: { id: number; kind: WorkOrderKind; label: string; hours: number | null }[] | null;
  /** DEC-395: có lệnh Làm Chủ nhật đã xác nhận */
  sundayWork?: boolean;
  /** DEC-265: vi phạm của ngày (đã qua); counted = còn tính chuyên cần */
  violations?: { kind: ViolationKind; minutes: number | null; id: number | null; status: ViolationStatus | null; counted: boolean }[] | null;
  /** DEC-760: trạng thái ngày Xuyên đêm / Về trễ / Hỗ trợ */
  marks?: { id: number; kind: DayMarkKind; status: DayMarkStatus; place: string | null }[] | null;
}

export interface TimesheetRow {
  employeeId: number;
  code: string;
  fullName: string;
  workGroup: WorkGroup;
  /** DEC-354 */
  division: Division | null;
  status: string;
  departmentName: string | null;
  crewName: string | null;
  /** DEC-247: địa điểm làm việc (1097 / 77 / 855) */
  locationName: string | null;
  asiaStaffCode: string | null;
  schedule: { id: number; name: string; calcMode: string; punchMode: PunchMode; startTime: string | null; gateInClose: string | null } | null;
  standardDays: number;
  totals: { work: number; presentDays: number; lateCount: number; lateMinutes: number; earlyMinutes: number; otHours: number; flagged: number; leavePaid?: number; leaveUnpaid?: number; sundayDays?: number; diligence?: Diligence; marks?: Record<DayMarkKind, number>; marksPending?: number; earlyHours?: number };
  cells: Record<string, TimesheetCell>;
}

export interface Timesheet {
  month: string;
  days: string[];
  locked: { lockedAt: string; lockedByName: string | null } | null;
  rows: TimesheetRow[];
}

export const useMyAttendance = () => useQuery({ queryKey: [...ATT_KEY, 'me'], queryFn: () => api.get<MyAttendance>('/me/attendance') });
export const useCrewToday = (id: number | null) =>
  useQuery({ queryKey: [...ATT_KEY, 'crew', id], queryFn: () => api.get<CrewToday>(`/hr/crews/${id}/today`), enabled: !!id });
export const useSchedules = (enabled = true) => useQuery({ queryKey: [...ATT_KEY, 'schedules'], queryFn: () => api.get<Schedule[]>('/hr/schedules'), enabled });
export const useWifiNetworks = (enabled = true) =>
  useQuery({ queryKey: [...ATT_KEY, 'wifi'], queryFn: () => api.get<WifiNetwork[]>('/hr/wifi-networks'), enabled });
/** P21 B4b – chính sách + bảng tăng ca tài xế (quy tắc minhtiencrm) */
export type DriverPolicyView = DriverPolicy & {
  version: number;
  updatedAt: string;
  /** Anh Tiến 05/11/2026: chính sách theo ngày hiệu lực */
  today?: string;
  versions?: { id: number; effectiveFrom: string; note: string | null; createdAt: string; createdByName: string | null }[];
};
export interface DriverPayDay {
  date: string;
  checkIn: string | null;
  checkOut: string | null;
  status: 'before_open' | 'early_ot' | 'on_time' | 'late' | 'closed' | null;
  plateNo: string | null;
  /** Xe của ca – mở "Xem GPS trưa" */
  vehicleId?: number | null;
  role: 'primary' | 'assistant' | null;
  work: number;
  lateMinutes: number;
  lunchKm: number | null;
  deliveries: string[];
  items: DriverPayItem[];
  amount: number;
  /** PR5: phiếu lệnh giao sau 17h trong ca, giờ về tối đa, phần dư, chuyến tỉnh, quên trả xe */
  after17?: { noteId: number; code: string; at: string; gpsOk: boolean; valid: boolean }[];
  hasOrder?: boolean;
  maxReturnAt?: string | null;
  excessMinutes?: number;
  excessApproved?: boolean;
  province?: 'start' | 'middle' | 'end' | 'single' | null;
  autoReturn?: boolean;
  /** DEC-767: giờ trả xe lấy theo "Giờ về bãi" đã duyệt trên bảng công */
  returnFromMark?: boolean;
  earlyHours?: number | null;
  /** DEC-769: giờ GPS xe rời bãi (bắt đầu đi làm) */
  departAt?: string | null;
}
export interface DriverPayRow {
  employeeId: number;
  code: string;
  fullName: string;
  linkedDriver: boolean;
  totals: Record<DriverPayType | 'work' | 'lateMinutes' | 'amount' | 'pickup' | 'trip' | 'otAmount' | 'deductAmount' | 'after17Orders' | 'forgotCount' | 'excessPending' | 'assistantLatePending', number>;
  warnings?: string[];
  days: DriverPayDay[];
}
export const useDriverPolicy = () => useQuery({ queryKey: [...ATT_KEY, 'driver-policy'], queryFn: () => api.get<DriverPolicyView>('/hr/driver-policy') });
export const useDriverPay = (month: string) =>
  useQuery({ queryKey: [...ATT_KEY, 'driver-pay', month], queryFn: () => api.get<{ month: string; rows: DriverPayRow[] }>('/hr/driver-pay', { month }) });
/** Bằng chứng GPS chạy xuyên trưa của 1 xe trong 1 ngày (khung + km tối thiểu theo Chính sách tài xế) */
export interface LunchGps {
  date: string;
  vehicleId: number;
  plateNo: string;
  lunchStart: string;
  lunchEnd: string;
  minKm: number;
  rate: number;
  km: number | null;
  reached: boolean;
  samples: { at: string; km: number; inWindow: boolean }[];
  positions: { at: string; lat: number; lng: number; speedKmh: number | null }[];
}
export const useLunchGps = (vehicleId: number | null, date: string | null) =>
  useQuery({
    queryKey: [...ATT_KEY, 'lunch-gps', vehicleId, date],
    queryFn: () => api.get<LunchGps>('/hr/driver-pay/lunch-gps', { vehicleId: vehicleId!, date: date! }),
    enabled: vehicleId !== null && !!date,
  });
export const useCrews = (enabled = true) => useQuery({ queryKey: [...ATT_KEY, 'crews'], queryFn: () => api.get<Crew[]>('/hr/crews'), enabled });

/** 'HH:MM' giờ VN */
export const hhmm = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Ho_Chi_Minh' }) : '';

/** Ảnh chấm công / ảnh mặt cần token → tải blob rồi tạo object URL. */
export function useAttendancePhotoSrc(id: number | null, size: 'thumb' | 'full' = 'thumb') {
  const q = useQuery({
    queryKey: ['attendance-photo', id, size],
    queryFn: () => api.blob(`/hr/attendance/photos/${id}/file`, { size }),
    enabled: !!id,
    staleTime: Infinity,
    gcTime: 10 * 60_000,
  });
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!q.data) return;
    const u = URL.createObjectURL(q.data);
    setSrc(u);
    return () => URL.revokeObjectURL(u);
  }, [q.data]);
  return src;
}

/** Chuẩn bị ảnh + vị trí + giờ chụp gốc cho 1 lượt chấm. */
export async function preparePunch(file: File) {
  const { data, thumb, lat, lng, sourceSha256 } = await prepareReportPhoto(file, { waitMs: 5000 });
  return { clientRef: crypto.randomUUID(), clientAt: new Date().toISOString(), photo: { data, thumb, sourceSha256 }, lat, lng };
}

// ---------------------------------------------------------------------------
// Hàng chờ khi mất mạng: lượt chấm lưu trên máy, có mạng thì tự gửi (giữ giờ chụp gốc, server nhận trong 24 giờ).
// clientRef giúp gửi lại không tạo trùng.
// ---------------------------------------------------------------------------
const QUEUE_KEY = 'mt_attendance_queue';
interface Queued {
  url: string;
  body: Record<string, unknown>;
  label: string;
}

function readQueue(): Queued[] {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]') as Queued[];
  } catch {
    return [];
  }
}
function writeQueue(q: Queued[]) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
    return true;
  } catch {
    return false;
  }
}

/** Gửi lượt chấm; mất mạng thì cất vào hàng chờ. Trả về true nếu đã gửi xong, false nếu đang chờ mạng. */
const DEVICE_KEY = 'mt.device.id';
/**
 * DEC-390: mã điện thoại chấm công, sinh 1 lần và lưu trên máy (đổi máy / xóa dữ liệu trình duyệt = máy mới).
 * Không đọc được bộ nhớ máy thì trả null (khóa đang tắt vẫn chấm được).
 */
export function deviceId(): string | null {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

export async function sendPunch(url: string, body: Record<string, unknown>, label: string): Promise<boolean> {
  // Tự chấm gửi kèm mã điện thoại (lượt lưu tạm khi mất mạng cũng giữ mã máy đã chụp)
  if (url === '/me/attendance' && body.deviceId === undefined) body = { ...body, deviceId: deviceId() };
  try {
    await api.post(url, body);
    return true;
  } catch (e) {
    if (e instanceof ApiError && e.status === 0) {
      if (!writeQueue([...readQueue(), { url, body, label }])) throw new Error('Mất mạng và máy hết chỗ lưu tạm. Chờ có mạng rồi chụp lại.');
      return false;
    }
    throw e;
  }
}

/** Số lượt đang chờ gửi + tự gửi khi có mạng lại. */
export function usePunchQueue() {
  const qc = useQueryClient();
  const [pending, setPending] = useState(() => readQueue().length);
  const flush = useCallback(async () => {
    const q = readQueue();
    if (!q.length) return setPending(0);
    const left: Queued[] = [];
    let sent = 0;
    for (const item of q) {
      try {
        await api.post(item.url, item.body);
        sent += 1;
      } catch (e) {
        if (e instanceof ApiError && e.status === 0) left.push(item);
        else toast.error(`${item.label}: ${(e as Error).message}`);
      }
    }
    writeQueue(left);
    setPending(left.length);
    if (sent) {
      toast.success(`Đã gửi ${sent} lượt chấm công lưu tạm`);
      void qc.invalidateQueries({ queryKey: ATT_KEY });
    }
  }, [qc]);
  useEffect(() => {
    void flush();
    const on = () => void flush();
    window.addEventListener('online', on);
    const t = setInterval(on, 60_000);
    return () => {
      window.removeEventListener('online', on);
      clearInterval(t);
    };
  }, [flush]);
  return { pending, refresh: () => setPending(readQueue().length), flush };
}

// ---------------------------------------------------------------------------
// P21 tài khoản chấm công (C3, C4) + mã NV Asia của NV
// ---------------------------------------------------------------------------
export interface AttendanceAccount {
  employeeId: number;
  code: string;
  fullName: string;
  workGroup: WorkGroup;
  division: Division | null;
  status: 'active' | 'suspended';
  departmentName: string | null;
  asiaStaffCode: string | null;
  asiaName: string | null;
  userId: number | null;
  username: string | null;
  userActive: boolean | null;
  mustChangePassword: boolean | null;
  lastLoginAt: string | null;
  roleCodes: string[];
  selfPunch: boolean;
  canManage: boolean;
}
export interface AccountList {
  items: AttendanceAccount[];
  counts: { noAccount: number; noAsia: number };
}
export interface CreateAccountResult {
  employeeId: number;
  code: string;
  fullName: string;
  status: 'created' | 'linked' | 'skipped';
  username: string | null;
  tempPassword?: string;
  message?: string;
}
export interface AsiaCodeOptions {
  options: { code: string; name: string; kind: 'login' | 'payroll'; employee: { id: number; code: string; fullName: string } | null }[];
  suggested: string[];
}
export const useAccounts = (f: { q?: string; division?: string; filter?: string }, enabled = true) =>
  useQuery({
    queryKey: [...ATT_KEY, 'accounts', f],
    queryFn: () => api.get<AccountList>('/hr/attendance-accounts', { q: f.q || undefined, division: f.division || undefined, filter: f.filter || undefined }),
    enabled,
  });
export const useAsiaCodeOptions = (employeeId: number | null) =>
  useQuery({
    queryKey: [...ATT_KEY, 'asia-options', employeeId],
    queryFn: () => api.get<AsiaCodeOptions>('/hr/asia-staff-options', { employeeId: employeeId ?? undefined }),
    enabled: employeeId !== null,
  });
/** Bảng công của chính mình (lịch tháng mini trên điện thoại) */
export const useMyTimesheet = (month: string, enabled = true) =>
  useQuery({ queryKey: [...ATT_KEY, 'my-sheet', month], queryFn: () => api.get<Timesheet>('/me/timesheet', { month }), enabled });

/** DEC-760 – trạng thái ngày Xuyên đêm / Về trễ / Hỗ trợ của 1 NV 1 ngày (panel bảng công) */
export interface DayMark {
  id: number;
  employeeId: number;
  workDate: string;
  kind: DayMarkKind;
  status: DayMarkStatus;
  place: string | null;
  note: string | null;
  requestedBy: number | null;
  requestedByName: string | null;
  requestedAt: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  decideNote: string | null;
  /** DEC-767: giờ về bãi (ISO) */
  returnAt: string | null;
  /** DEC-768: số giờ Đi sớm */
  hours: number | null;
  /** DEC-769: 'gps' = hệ thống tự ghi theo GPS xe; DEC-930: 'early_order' = tự đề nghị Đi sớm theo đơn giao sớm (chờ duyệt) */
  source?: 'manual' | 'gps' | 'early_order';
  version: number;
}
export interface DayMarksView {
  items: DayMark[];
  canDecide: boolean;
  canPropose: boolean;
  locationName: string | null;
}
export const useDayMarks = (employeeId: number, date: string) =>
  useQuery({ queryKey: [...ATT_KEY, 'day-marks', employeeId, date], queryFn: () => api.get<DayMarksView>('/hr/day-marks', { employeeId, date }) });
